// frontend/src/utils/subdomain.js
// =============================================
// All subdomain detection & URL building lives here.
// Import this anywhere you need hotel/subdomain context.
// =============================================

const BASE_DOMAIN = process.env.REACT_APP_BASE_DOMAIN || 'staynivo.com';
const BASE_DOMAINS = [BASE_DOMAIN, 'localhost'];

/**
 * Returns the current subdomain from the browser URL.
 *
 * Examples:
 *   grandhorizon.staynivo.com  → 'grandhorizon'
 *   grandhorizon.localhost   → 'grandhorizon'
 *   staynivo.com               → null
 *   localhost:3000           → null
 */
export const getSubdomain = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // grandhorizon.staynivo.com (3 parts)
  if (parts.length >= 3) {
    const sub = parts[0].toLowerCase();
    if (!isReserved(sub)) return sub;
  }

  // grandhorizon.localhost (2 parts, second is localhost)
  if (parts.length === 2 && parts[1] === 'localhost') {
    const sub = parts[0].toLowerCase();
    if (!isReserved(sub)) return sub;
  }

  return null;
};

/**
 * Returns whether we are currently on a hotel subdomain.
 */
export const isOnSubdomain = () => !!getSubdomain();

/**
 * Checks if we are on the main marketing/platform domain
 * (no subdomain = landing page / super admin area).
 */
export const isOnMainDomain = () => !getSubdomain();

/**
 * Build a full URL for a given hotel subdomain.
 * e.g. buildSubdomainUrl('grandhorizon') → 'https://grandhorizon.staynivo.com'
 */
export const buildSubdomainUrl = (subdomain, path = '') => {
  const isLocalhost = window.location.hostname.includes('localhost');
  const port = window.location.port;

  if (isLocalhost) {
    return `http://${subdomain}.localhost${port ? ':' + port : ''}${path}`;
  }
  return `https://${subdomain}.${BASE_DOMAIN}${path}`;
};

/**
 * Build the API base URL for the current subdomain.
 * In dev: uses REACT_APP_API_URL env (proxied by CRA).
 * In prod: uses the subdomain's own domain.
 */
export const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;

  const subdomain = getSubdomain();
  const isLocalhost = window.location.hostname.includes('localhost');

  if (isLocalhost) return 'http://localhost:5000/api';

  if (subdomain) return `https://${subdomain}.${BASE_DOMAIN}/api`;
  return `https://api.${BASE_DOMAIN}/api`;
};

/**
 * Returns the current hotel subdomain for display purposes.
 */
export const getHotelDisplayName = (hotelName) => {
  const sub = getSubdomain();
  return hotelName || sub || 'StayNivo';
};

/**
 * Reserved subdomains that cannot be hotel names.
 */
const RESERVED = new Set([
  'www','api','app','admin','superadmin','mail','smtp','ftp',
  'static','assets','cdn','help','support','blog','docs',
  'status','dev','staging','demo','test',
]);

export const isReserved = (subdomain) => RESERVED.has(subdomain?.toLowerCase());

/**
 * Validate a subdomain string.
 * Returns { valid: boolean, error?: string }
 */
export const validateSubdomain = (subdomain) => {
  if (!subdomain) return { valid: false, error: 'Subdomain is required' };
  const lower = subdomain.toLowerCase();
  if (isReserved(lower)) return { valid: false, error: `"${lower}" is a reserved word` };
  if (lower.length < 3) return { valid: false, error: 'Minimum 3 characters' };
  if (lower.length > 63) return { valid: false, error: 'Maximum 63 characters' };
  if (!/^[a-z0-9][a-z0-9\-]*[a-z0-9]$/.test(lower)) {
    return { valid: false, error: 'Only lowercase letters, numbers, and hyphens. Cannot start or end with a hyphen.' };
  }
  return { valid: true };
};

/**
 * Redirect to a specific hotel's subdomain.
 * e.g. after super admin creates a new hotel.
 */
export const redirectToSubdomain = (subdomain, path = '/dashboard') => {
  const url = buildSubdomainUrl(subdomain, path);
  window.location.href = url;
};

/**
 * Redirect to main platform domain (landing / super admin).
 */
export const redirectToMainDomain = (path = '/') => {
  const isLocalhost = window.location.hostname.includes('localhost');
  const port = window.location.port;
  const url = isLocalhost
    ? `http://localhost${port ? ':' + port : ''}${path}`
    : `https://${BASE_DOMAIN}${path}`;
  window.location.href = url;
};
